// Module 2 — Placement.
// Route: /reinsurance/placement
//
// Market approach, capacity and quoting. The same deals the Submissions
// screen shows, once they have gone out to market.
//
// Distinct from the older /reinsurance/placement/queue endpoint, which
// answers "what needs a chase today". This screen answers "what is in the
// market, and how much of it is covered".

import React from 'react';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import PlacementDrawer from './PlacementDrawer';
import {
  C, Icons, Kpi, Pill, Bar,
  PrimaryButton, GhostButton, TwoLine, money0, moneyMixed, date, humanise,
} from './kit';

function TypeChip({ value }) {
  if (!value) return <span style={{ color: C.faint }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 6,
      border: `1px solid ${C.border}`, background: C.panel,
      fontSize: 11.5, fontWeight: 600, color: C.body, whiteSpace: 'nowrap',
    }}>
      {humanise(value)}
    </span>
  );
}

// Quoted rate on line, as a bar plus the figure. Null is rendered as a dash
// rather than an empty bar: a placement nobody has quoted yet is not a
// placement quoted at zero.
function RolCell({ value }) {
  if (value === null || value === undefined) return <span style={{ color: C.faint }}>—</span>;
  const n = Math.max(0, Math.min(100, Number(value)));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
      <Bar percent={n} width={52} tone={n >= 90 ? C.bar : n >= 50 ? '#f59e0b' : '#ef4444'} />
      <span style={{ fontSize: 12, color: C.body, fontVariantNumeric: 'tabular-nums' }}>{Math.round(n)}%</span>
    </span>
  );
}

// Opening a placement: the cedant, what is being placed, and how much
// capacity is wanted. Markets are added on the board afterwards - a
// placement exists before anyone has been approached, which is the whole
// point of the Early Discussions status.
const placementForm = (t) => ({
  title: t('screens.rePlacement.newTitle', 'New placement'),
  subtitle: t('screens.rePlacement.newSub', 'Open a risk to the market'),
  submitLabel: t('screens.rePlacement.create', 'Open placement'),
  endpoint: '/reinsurance/placements',
  fields: [
    { key: 'cedantName', label: t('screens.rePlacement.fCedantIn', 'Cedant'), required: true,
      placeholder: 'Qatar Insurance Co.' },
    { key: 'dealType', label: t('screens.rePlacement.fTypeIn', 'Type'), type: 'select', required: true,
      defaultValue: 'treaty',
      options: [
        { value: 'treaty', label: 'Treaty' },
        { value: 'facultative', label: 'Facultative' },
      ] },
    { key: 'lineOfBusiness', label: t('screens.rePlacement.fClassIn', 'Class'), required: true,
      placeholder: 'Property' },
    // QA 2026-09-21: the risk, the structure and the sum insured were all
    // absent from this form, so a placement could be opened without saying
    // what was being placed or how it was structured.
    { key: 'insuredName', label: t('screens.rePlacement.fRiskIn', 'Risk / insured'),
      placeholder: 'Ras Laffan LNG Terminal',
      help: t('screens.rePlacement.fRiskHelp', 'What is being placed — the named risk or original insured.') },
    { key: 'structureType', label: t('screens.rePlacement.fStructureIn', 'Structure'), type: 'select',
      defaultValue: '',
      options: [
        { value: '', label: 'Not stated' },
        { value: 'quota_share', label: 'Quota Share' },
        { value: 'surplus', label: 'Surplus Share' },
        { value: 'excess_of_loss', label: 'Excess of Loss' },
        { value: 'stop_loss', label: 'Stop Loss' },
        { value: 'facultative', label: 'Per Risk' },
      ] },
    { key: 'sumInsured', label: t('screens.rePlacement.fSumIn', 'Sum insured'), type: 'number',
      placeholder: '250000000' },
    { key: 'layerLabel', label: t('screens.rePlacement.fLayerIn', 'Layer'),
      placeholder: 'XS 100M xs 50M',
      help: t('screens.rePlacement.fLayerHelp', 'Free text — how the market will refer to this line.') },
    { key: 'geography', label: t('screens.rePlacement.fGeoIn', 'Geography'), placeholder: 'Qatar' },
    { key: 'capacityRequired', label: t('screens.rePlacement.fCapacityIn', 'Capacity required'), type: 'number',
      required: true, placeholder: '75000000' },
    { key: 'premium', label: t('screens.rePlacement.fPremiumIn', 'Premium'), type: 'number',
      placeholder: '375000',
      help: t('screens.rePlacement.fPremiumHelp', 'Each signed line is billed its share of this. Without it the closing has nothing to settle.') },
    { key: 'currency', label: t('screens.rePlacement.fCurrencyIn', 'Currency'), defaultValue: 'USD' },
    { key: 'targetCloseDate', label: t('screens.rePlacement.fCloseIn', 'Target close'), type: 'date' },
    { key: 'placementStatus', label: t('screens.rePlacement.fStatusIn', 'Status'), type: 'select',
      defaultValue: 'early_discussions',
      options: [
        { value: 'early_discussions', label: 'Early Discussions' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'quotes_received', label: 'Quotes Received' },
        { value: 'under_review', label: 'Under Review' },
      ],
      help: t('screens.rePlacement.fStatusHelp', 'Bound is set by signing a line, not chosen here.') },
  ],
  toPayload: (v) => ({
    cedantName: v.cedantName.trim(),
    dealType: v.dealType,
    lineOfBusiness: v.lineOfBusiness.trim(),
    ...(v.layerLabel ? { layerLabel: v.layerLabel.trim() } : {}),
    ...(v.geography ? { geography: v.geography.trim(), territory: v.geography.trim() } : {}),
    ...(v.insuredName ? { insuredName: v.insuredName.trim() } : {}),
    ...(v.structureType ? { structureType: v.structureType } : {}),
    ...(v.sumInsured !== '' && v.sumInsured !== undefined ? { sumInsured: Number(v.sumInsured) } : {}),
    capacityRequired: Number(v.capacityRequired),
    ...(v.premium !== '' && v.premium !== undefined ? { premium: Number(v.premium) } : {}),
    currency: (v.currency || 'USD').trim().toUpperCase(),
    ...(v.targetCloseDate ? { targetCloseDate: v.targetCloseDate } : {}),
    placementStatus: v.placementStatus || 'early_discussions',
  }),
});

export default function PlacementList() {
  const { t } = useTranslation();

  const columns = [
    { key: 'reference', label: t('screens.rePlacement.colRef', 'Placement ref'), width: 150,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.reference}</span> },
    { key: 'cedantName', label: t('screens.rePlacement.colCedant', 'Cedant'),
      render: (r) => <TwoLine primary={r.cedantName} secondary={r.geography || r.territory} /> },
    { key: 'dealType', label: t('screens.rePlacement.colType', 'Type'), width: 104,
      render: (r) => <TypeChip value={r.dealType} /> },
    { key: 'lineOfBusiness', label: t('screens.rePlacement.colClass', 'Class'),
      render: (r) => r.lineOfBusiness || '—' },
    { key: 'layerLabel', label: t('screens.rePlacement.colLayer', 'Layer'),
      render: (r) => r.layerLabel || r.structureType || '—' },
    // Markets approached, not markets listed. A row added to the slip but
    // never sent out has not been approached, and counting it would overstate
    // how far the placement has got.
    { key: 'approachedCount', label: t('screens.rePlacement.colMarkets', 'Markets'), numeric: true, width: 78,
      render: (r) => r.approachedCount ?? 0 },
    { key: 'capacityRequired', label: t('screens.rePlacement.colCapacity', 'Capacity'), numeric: true,
      render: (r) => money0(r.capacityRequired, r.currency) },
    { key: 'capacityPct', label: t('screens.rePlacement.colSecured', 'Secured'), width: 110,
      render: (r) => (r.capacityPct === null || r.capacityPct === undefined
        ? <span style={{ color: C.faint }}>—</span>
        : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
            <Bar percent={r.capacityPct} width={52} />
            <span style={{ fontSize: 12, color: C.body, fontVariantNumeric: 'tabular-nums' }}>{Math.round(r.capacityPct)}%</span>
          </span>
        )) },
    { key: 'quotedRol', label: t('screens.rePlacement.colRol', 'Quoted ROL'), width: 112,
      render: (r) => <RolCell value={r.quotedRol} /> },
    { key: 'targetCloseDate', label: t('screens.rePlacement.colClose', 'Target close'), width: 112,
      render: (r) => date(r.targetCloseDate) },
    { key: 'placementStatus', label: t('screens.rePlacement.colStatus', 'Status'), width: 134,
      render: (r) => <Pill status={r.placementStatus} /> },
  ];

  return (
    <ModulePage
      title={t('screens.rePlacement.title', 'Placement')}
      subtitle={t('screens.rePlacement.subtitle', 'Market approach, capacity and quoting')}
      endpoint='/reinsurance/placements'
      searchPlaceholder={t('screens.rePlacement.search', 'Search by cedant, placement ref, class or reinsurer…')}
      createForm={placementForm(t)}
      actions={({ openCreate }) => (
        <>
          <GhostButton icon={Icons.users()}>{t('screens.rePlacement.addMarket', 'Add reinsurer')}</GhostButton>
          <PrimaryButton onClick={openCreate}>
            {t('screens.rePlacement.issue', 'New placement')}
          </PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.doc()} tone='blue'
            value={s?.openPlacements?.value ?? '—'} trend={s?.openPlacements?.trend}
            label={t('screens.rePlacement.kpiOpen', 'Open placements')} />
          <Kpi icon={Icons.send()} tone='green'
            value={s?.marketsApproached?.value ?? '—'} trend={s?.marketsApproached?.trend}
            label={t('screens.rePlacement.kpiApproached', 'Markets approached')} />
          <Kpi icon={Icons.check()} tone='amber'
            value={s?.firmLinesQuoted?.value ?? '—'} trend={s?.firmLinesQuoted?.trend}
            label={t('screens.rePlacement.kpiFirm', 'Firm lines quoted')} />
          <Kpi icon={Icons.db()} tone='blue'
            value={moneyMixed(s?.capacitySecured)} trend={s?.capacitySecured?.trend}
            comparison={s?.capacitySecured?.comparison}
            label={t('screens.rePlacement.kpiCapacity', 'Capacity secured')} />
        </>
      )}
      filters={[
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'early_discussions', label: 'Early Discussions' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'quotes_received', label: 'Quotes Received' },
          { value: 'under_review', label: 'Under Review' },
          { value: 'bound', label: 'Bound' },
          { value: 'declined', label: 'Declined' },
        ] },
        { key: 'type', label: 'Type', options: [
          { value: 'all', label: 'All types' },
          { value: 'treaty', label: 'Treaty' },
          { value: 'facultative', label: 'Facultative' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.rePlacement.empty', 'Nothing in the market. Mark a submission ready for placement to start one.')}
      drawerTabs={['Overview', 'Market Responses', 'Structure', 'Activity']}
      drawerHeader={(r) => ({
        title: r.reference,
        badge: <Pill status={r.placementStatus} />,
        lines: [
          r.cedantName,
          [humanise(r.dealType), r.lineOfBusiness, r.geography || r.territory].filter(Boolean).join(' · '),
        ].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <PlacementDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
